import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import { ChefHat, MapPin, Phone, Mail, Clock, Instagram, Facebook, Twitter, Youtube, ExternalLink, ShieldCheck } from 'lucide-react';
import { cleanLogoUrl } from '../utils/logo';

export default function Footer() {
    const location = useLocation();
    const { tenant, isFranchise, franchise, branches } = useTenant();
    const [activePolicy, setActivePolicy] = useState(null); // null | 'privacy' | 'terms' | 'refund'

    // Do not show storefront footer on dashboard or kitchen/delivery routes
    const isDashboardRoute = location.pathname.startsWith('/admin') || 
                             location.pathname.startsWith('/kitchen') || 
                             location.pathname.startsWith('/delivery');

    if (isDashboardRoute) return null;

    const isAtFranchiseLevel = isFranchise && !location.pathname.startsWith('/branch/');
    const activeBrand = isAtFranchiseLevel ? franchise : tenant;
    const hs = activeBrand?.home_settings || {};

    const restaurantName = activeBrand?.name || (isAtFranchiseLevel ? 'Our Franchise Brand' : 'Savory Bites Restaurant');
    const restaurantAddress = hs.locationAddress || activeBrand?.address || (isAtFranchiseLevel ? 'Corporate Headquarters' : '123 Gourmet Boulevard, Foodie Haven, Chennai, Tamil Nadu');
    const restaurantPhone = hs.contactPhone || activeBrand?.contact_phone || (isAtFranchiseLevel ? 'Corporate Hotline' : '+91 98765 43210');
    const restaurantEmail = hs.contactEmail || activeBrand?.contact_email || (isAtFranchiseLevel ? 'info@franchise.com' : 'contact@savorybites.com');
    const fssaiLicense = hs.fssaiLicense || '12422020000145';
    const mapUrl = hs.mapEmbedUrl || `https://maps.google.com/?q=${encodeURIComponent(restaurantAddress)}`;

    const operatingHours = Array.isArray(hs.operatingHours) && hs.operatingHours.length > 0 ? hs.operatingHours : [
        { day: 'Mon - Thu', time: hs.monThuHours || '11:00 AM - 10:30 PM' },
        { day: 'Fri - Sat', time: hs.friSatHours || '11:00 AM - 11:30 PM' },
        { day: 'Sun', time: hs.sunHours || '12:00 PM - 10:00 PM' }
    ];

    const footerBg = hs.footerBgColor ? { backgroundColor: hs.footerBgColor } : (activeBrand?.secondary_color ? { backgroundColor: activeBrand.secondary_color } : {});
    const footerTextColorStyle = hs.footerTextColor ? { color: hs.footerTextColor } : {};
    const footerHeadingColorStyle = hs.footerHeadingColor ? { color: hs.footerHeadingColor } : {};
    const footerTimeColorStyle = hs.footerTimeColor ? { color: hs.footerTimeColor } : (activeBrand?.primary_color ? { color: activeBrand.primary_color } : { color: '#fbbf24' });
    const footerSecondaryColorStyle = hs.footerSecondaryColor ? { color: hs.footerSecondaryColor } : { color: '#94A3B8' };

    const policies = [
        { key: 'privacy', label: 'Privacy Policy', content: hs.privacyPolicy },
        { key: 'terms', label: 'Terms of Service', content: hs.termsOfService },
        { key: 'refund', label: 'Refund & Cancellation Policy', content: hs.refundPolicy },
    ].filter(p => p.content && p.content.trim().length > 0);

    const activePolicyData = policies.find(p => p.key === activePolicy);

    const getHomeLink = () => {
        if (location.pathname.startsWith('/branch/')) {
            const parts = location.pathname.split('/');
            if (parts[2]) {
                return `/branch/${parts[2]}`;
            }
        }
        const activeBranch = localStorage.getItem('active_branch_slug');
        if (activeBranch) {
            return `/branch/${activeBranch}`;
        }
        if (tenant?.slug) {
            return `/?restaurant=${tenant.slug}`;
        }
        return '/';
    };

    return (
        <>
        <footer className="bg-slate-900 text-slate-300 pt-8 pb-6 border-t border-slate-800" style={{ ...footerBg, ...footerTextColorStyle }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
                {isAtFranchiseLevel ? (
                    // FRANCHISE PORTAL FOOTER CONTENT
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {/* Brand & Tagline */}
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                {tenant?.logo_url ? (
                                    <img src={cleanLogoUrl(tenant.logo_url)} alt={restaurantName} className="h-10 w-auto object-contain bg-white/10 p-1.5 rounded-xl" />
                                ) : (
                                    <div className="w-10 h-10 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                                        <ChefHat className="w-6 h-6" />
                                    </div>
                                )}
                                <span className="text-xl font-black text-white tracking-tight" style={footerHeadingColorStyle}>{restaurantName}</span>
                            </div>
                            <p className="text-xs leading-relaxed" style={footerSecondaryColorStyle}>
                                {hs.heroSubtitle || `Authentic culinary experiences delivered across multiple premium branch locations. Welcome to our official brand portal.`}
                            </p>
                        </div>

                        {/* Our Branches Directory */}
                        <div className="space-y-4">
                            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2" style={footerHeadingColorStyle}>Our Branches</h4>
                            <ul className="space-y-2.5 text-xs">
                                {branches && branches.length > 0 ? (
                                    branches.slice(0, 6).map(branch => (
                                        <li key={branch.id}>
                                            <Link to={`/branch/${branch.slug}`} className="hover:opacity-80 transition-opacity flex items-center gap-1.5" style={footerTextColorStyle}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                                <span>{branch.name}</span>
                                                <span className="text-[10px] text-slate-500 font-mono">({branch.city || 'Chennai'})</span>
                                            </Link>
                                        </li>
                                    ))
                                ) : (
                                    <li className="text-slate-500 italic">No active branches configured.</li>
                                )}
                            </ul>
                        </div>

                        {/* Corporate Headquarters */}
                        <div className="space-y-4">
                            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2" style={footerHeadingColorStyle}>Corporate Enquiries</h4>
                            <div className="space-y-3 text-xs text-slate-400" style={footerTextColorStyle}>
                                <div className="flex items-start space-x-2.5">
                                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: tenant?.primary_color || '#fbbf24' }} />
                                    <span>{restaurantAddress}</span>
                                </div>
                                <div className="flex items-center space-x-2.5">
                                    <Phone className="w-4 h-4 flex-shrink-0" style={{ color: tenant?.primary_color || '#fbbf24' }} />
                                    <a href={`tel:${restaurantPhone}`} className="hover:underline" style={footerTextColorStyle}>{restaurantPhone}</a>
                                </div>
                                <div className="flex items-center space-x-2.5">
                                    <Mail className="w-4 h-4 flex-shrink-0" style={{ color: tenant?.primary_color || '#fbbf24' }} />
                                    <a href={`mailto:${restaurantEmail}`} className="hover:underline" style={footerTextColorStyle}>{restaurantEmail}</a>
                                </div>
                            </div>
                        </div>

                        {/* Franchise Support & Partners */}
                        <div className="space-y-4">
                            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2" style={footerHeadingColorStyle}>Partnership</h4>
                            <p className="text-xs text-slate-400 leading-relaxed" style={footerTextColorStyle}>
                                Interested in owning a franchise or registering your restaurant branch? Contact our management team for corporate queries.
                            </p>
                            <div className="pt-1">
                                <a href={`mailto:${restaurantEmail}`} className="inline-flex items-center space-x-1.5 text-[11px] font-bold hover:underline" style={{ color: tenant?.primary_color || '#fbbf24' }}>
                                    <span>Corporate Collaboration</span>
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        </div>
                    </div>
                ) : (
                    // STANDARD BRANCH FOOTER CONTENT
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Brand & Tagline */}
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                {tenant?.logo_url ? (
                                    <img src={cleanLogoUrl(tenant.logo_url)} alt={restaurantName} className="h-10 w-auto object-contain bg-white/10 p-1.5 rounded-xl" />
                                ) : (
                                    <div className="w-10 h-10 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                                        <ChefHat className="w-6 h-6" />
                                    </div>
                                )}
                                <span className="text-xl font-black text-white tracking-tight" style={footerHeadingColorStyle}>{restaurantName}</span>
                            </div>
                            <p className="text-xs leading-relaxed" style={footerSecondaryColorStyle}>
                                {hs.heroSubtitle || 'Delivering piping hot, handcrafted meals made with authentic ingredients and hygiene standards.'}
                            </p>
                            <div className="pt-2 flex items-center space-x-2 text-xs p-3 rounded-xl border w-fit font-semibold" style={{ color: hs.footerTimeColor || activeBrand?.primary_color || '#fbbf24', backgroundColor: (hs.footerTimeColor || activeBrand?.primary_color || '#fbbf24') + '10', borderColor: (hs.footerTimeColor || activeBrand?.primary_color || '#fbbf24') + '30' }}>
                                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                                <span>FSSAI Lic. No: <strong style={{ color: hs.footerTimeColor || activeBrand?.primary_color || '#fbbf24' }}>{fssaiLicense}</strong></span>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="space-y-4">
                            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2" style={footerHeadingColorStyle}>Quick Navigation</h4>
                            <ul className="space-y-2.5 text-xs">
                                <li><Link to={getHomeLink()} className="hover:opacity-80 transition-opacity" style={footerTextColorStyle}>Home</Link></li>
                                <li><Link to="/menu" className="hover:opacity-80 transition-opacity" style={footerTextColorStyle}>Explore Menu</Link></li>
                                <li><a href="#location-hours" className="hover:opacity-80 transition-opacity" style={footerTextColorStyle}>Operating Hours & Location</a></li>
                                <li><Link to="/orders" className="hover:opacity-80 transition-opacity" style={footerTextColorStyle}>Track Your Order</Link></li>
                            </ul>
                        </div>

                        {/* Operating Hours Summary */}
                        <div className="space-y-4">
                            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2 flex items-center gap-2" style={footerHeadingColorStyle}>
                                <Clock className="w-3.5 h-3.5" style={footerTimeColorStyle} /> Operating Hours
                            </h4>
                            <div className="space-y-2 text-xs">
                                {operatingHours.slice(0, 3).map((oh, idx) => (
                                    <div key={idx} className="flex justify-between items-center bg-slate-850/50 p-2 rounded-lg border border-slate-800">
                                        <span className="font-bold" style={footerSecondaryColorStyle}>{oh.day}</span>
                                        <span className="font-mono text-[11px]" style={footerTimeColorStyle}>{oh.time}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Contact & Location */}
                        <div className="space-y-4">
                            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2" style={footerHeadingColorStyle}>Find & Contact Us</h4>
                            <div className="space-y-3 text-xs" style={footerSecondaryColorStyle}>
                                <div className="flex items-start space-x-2.5">
                                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={footerTimeColorStyle} />
                                    <span>{restaurantAddress}</span>
                                </div>
                                <div className="flex items-center space-x-2.5">
                                    <Phone className="w-4 h-4 flex-shrink-0" style={footerTimeColorStyle} />
                                    <a href={`tel:${restaurantPhone}`} className="hover:underline" style={footerSecondaryColorStyle}>{restaurantPhone}</a>
                                </div>
                                <div className="flex items-center space-x-2.5">
                                    <Mail className="w-4 h-4 flex-shrink-0" style={footerTimeColorStyle} />
                                    <a href={`mailto:${restaurantEmail}`} className="hover:underline" style={footerSecondaryColorStyle}>{restaurantEmail}</a>
                                </div>
                                <div className="pt-1">
                                    <a href={mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center space-x-1.5 text-[11px] font-bold hover:underline" style={footerTimeColorStyle}>
                                        <span>Open Google Maps Directions</span>
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Social Connect & Copyright */}
                <div className="pt-4 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-500">
                    <div className="flex items-center space-x-4">
                        {hs.displaySocialIcons !== false && hs.showInstagram !== false && hs.instagramUrl && (
                            <a href={hs.instagramUrl} target="_blank" rel="noreferrer" className="p-2 bg-slate-800 hover:bg-primary-600 hover:text-white rounded-xl transition-colors"><Instagram className="w-4 h-4" /></a>
                        )}
                        {hs.displaySocialIcons !== false && hs.showFacebook !== false && hs.facebookUrl && (
                            <a href={hs.facebookUrl} target="_blank" rel="noreferrer" className="p-2 bg-slate-800 hover:bg-blue-600 hover:text-white rounded-xl transition-colors"><Facebook className="w-4 h-4" /></a>
                        )}
                        {hs.displaySocialIcons !== false && hs.showTwitter !== false && hs.twitterUrl && (
                            <a href={hs.twitterUrl} target="_blank" rel="noreferrer" className="p-2 bg-slate-800 hover:bg-slate-700 hover:text-white rounded-xl transition-colors"><Twitter className="w-4 h-4" /></a>
                        )}
                        {hs.displaySocialIcons !== false && hs.showYoutube !== false && hs.youtubeUrl && (
                            <a href={hs.youtubeUrl} target="_blank" rel="noreferrer" className="p-2 bg-slate-800 hover:bg-red-600 hover:text-white rounded-xl transition-colors"><Youtube className="w-4 h-4" /></a>
                        )}
                    </div>
                    <div className="text-center sm:text-right space-y-1">
                        <p>© {new Date().getFullYear()} {restaurantName}. All rights reserved.</p>
                        <p className="text-[10px] text-slate-600">Powered by <span className="font-bold text-slate-400">Aaharam Cloud SaaS</span></p>
                        {policies.length > 0 && (
                            <div className="flex flex-wrap justify-center sm:justify-end gap-3 pt-1">
                                {policies.map(p => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        onClick={() => setActivePolicy(p.key)}
                                        className="text-[10px] text-slate-500 hover:text-slate-300 underline underline-offset-2 transition-colors"
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </footer>

        {/* Policy Modal */}
        {activePolicy && activePolicyData && (
            <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[85vh]">
                    <div className="bg-slate-950 p-5 text-white text-center flex-shrink-0">
                        <h3 className="text-lg font-black uppercase tracking-wider">{activePolicyData.label}</h3>
                        <p className="text-[11px] text-slate-400 mt-1">{restaurantName}</p>
                    </div>
                    <div className="p-6 overflow-y-auto flex-1 text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                        {activePolicyData.content}
                    </div>
                    <div className="p-4 border-t border-slate-100 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setActivePolicy(null)}
                            className="w-full bg-slate-900 text-white font-black uppercase tracking-widest text-xs py-3 rounded-xl shadow-lg hover:bg-slate-800 transition-all"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        )}
    </>
    );
}
